import { IconExternalLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { renderArticleDate } from "../../domains/app/utils/renderDate";
import type { AudioMediaDataModel } from "../../domains/app/contexts/audio.context";
import {
  doiUrl,
  externalSourceUrl,
} from "../../domains/article/utils/sourceUrl";
import { openExternalUrl } from "../../tools/open-external-url";

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-dm-sans text-caption font-semibold uppercase tracking-[0.66px] text-ds-slate-500">
        {label}
      </span>
      <span className="font-dm-sans text-footnote text-ds-slate-950">
        {children}
      </span>
    </div>
  );
}

/**
 * Compact publication metadata for the expanded AudioPlayer sheet. Renders
 * only the fields present on the current track.
 */
export default function AudioTrackInfo({
  media,
  onExternalClick,
}: {
  media: AudioMediaDataModel;
  onExternalClick: (url: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const content = media.source;
  const articleSource = media.type === "article_audio" ? media.source : null;
  const dateObject =
    articleSource !== null && typeof articleSource.publication_date === "string"
      ? new Date(articleSource.publication_date)
      : null;
  const journal =
    articleSource !== null && articleSource.journal !== null
      ? articleSource.journal.name
      : null;
  const primaryAuthor =
    media.type === "article_audio" && media.source.primary_author !== null
      ? media.source.primary_author
      : null;
  const otherAuthors =
    media.type === "article_audio" && media.source.authors.length > 0
      ? media.source.authors
      : null;
  const doiHref = content.doi === null ? null : doiUrl(content.doi);
  const externalUrl =
    doiHref !== null
      ? doiHref
      : content.externalLink === null
        ? null
        : externalSourceUrl(content.externalLink);

  return (
    <div className="flex w-full flex-col gap-3 text-left">
      {dateObject === null ? null : (
        <InfoRow label={t("Publication Date")}>
          {renderArticleDate(dateObject)}
        </InfoRow>
      )}
      {journal === null ? null : (
        <InfoRow label={t("Journal")}>{journal}</InfoRow>
      )}
      {primaryAuthor === null ? null : (
        <InfoRow label={t("Primary author")}>{primaryAuthor}</InfoRow>
      )}
      {otherAuthors === null ? null : (
        <InfoRow label={t("Other authors")}>{otherAuthors.join(", ")}</InfoRow>
      )}
      {content.doi === null || doiHref === null ? null : (
        <InfoRow label="DOI">
          <a
            href={doiHref}
            target="_blank"
            rel="noreferrer"
            className="underline"
            onClick={() => {
              onExternalClick(doiHref);
            }}
          >
            {content.doi}
          </a>
        </InfoRow>
      )}

      <div className="flex flex-col gap-1 border-t border-ds-slate-200 pt-3">
        <span className="font-dm-sans text-footnote font-semibold text-ds-slate-500">
          {t("Content Liability")}
        </span>
        <span className="font-dm-sans text-caption leading-tight text-ds-slate-500">
          {t(
            "This content is a summary (potentially AI-assisted) of the original source article, provided for informational purposes. It may contain simplifications or errors. For any clinical or research application, please refer to the complete source document, available below."
          )}
        </span>
      </div>

      {externalUrl === null ? null : (
        <button
          type="button"
          onClick={() => {
            onExternalClick(externalUrl);
            // `window.open` opens nothing inside a native WebView. The shells
            // route an external link to the system browser through this helper.
            openExternalUrl(externalUrl);
          }}
          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-solid border-ds-slate-200 px-4 py-3 text-left outline-none cursor-pointer"
        >
          <span className="font-dm-sans text-footnote font-medium text-ds-slate-950">
            {t("More information about this publication")}
          </span>
          <IconExternalLink size={18} className="shrink-0 text-ds-slate-500" />
        </button>
      )}
    </div>
  );
}
